import { useState,useEffect } from "react";
import { loadCaptchaEnginge, LoadCanvasTemplate, validateCaptcha } from 'react-simple-captcha';
import axios from "axios";
import bcrypt from "bcryptjs";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'
import { useNavigate } from "react-router-dom";
import { GlobalLoading, showLoading } from 'react-global-loading';
import { API_URL, API_KEY }  from '../Constant';
import { AUTH_USE_HTTPONLY_COOKIE } from '../config/authFlags';
import {
  resolveUserAfterSignin,
  extractAccessTokenFromPayload,
} from '../utils/authSessionApi';
import { setToken } from '../utils/authStorage';
import { setCsrfToken } from '../utils/csrf';
import platform from "platform";

/** Single message for invalid username/password/both — prevents username enumeration */
const LOGIN_INVALID_CREDENTIALS_MSG =
  "Wrong Username or Password.";

const isSignInUrl = (config) => {
  const url = config?.url || "";
  return url.includes("/api/auth/signin") || url.includes("auth/signin");
};

const isNonceUrl = (config) => {
  const url = config?.url || "";
  return url.includes("/api/security/nonce") || url.includes("security/nonce");
};


/** True when failure is almost certainly “bad login”, not nonce/CORS/etc. */
const isCredentialFailureResponse = (error) => {
  const res = error?.response;
  if (!res) return false;
  const status = res.status;
  const data = res.data || {};
  const msg = String(data.message || data.error || "").toLowerCase();

  if (!isSignInUrl(error.config || res.config)) return false;

  if (status === 401 || status === 403) return true;

  const hints =
    msg.includes("incorrect password") ||
    msg.includes("wrong password") ||
    msg.includes("invalid password") ||
    msg.includes("user_id not found") ||
    msg.includes("userid not found") ||
    msg.includes("user not found") ||
    msg.includes("invalid credentials") ||
    msg.includes("authentication failed") ||
    msg.includes("invalid user");

  if (hints) return true;

  if (status === 400 || status === 404) return true;

  return false;
};

const Login = ({loginCallToParent}) => {


 const tosterStyle= {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
      };

  const [userId, setUserId] = useState("");
  const [pwd, setPassword] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  /**
   * After a failed login: clear sensitive fields + new captcha so user can retry immediately.
   * Inputs are never disabled (audit: no DoS / locked form).
   */
  const resetFormAfterFailedLogin = (opts = { clearUserId: true }) => {
    if (opts.clearUserId) setUserId("");
    setPassword("");
    setCaptcha("");
    loadCaptchaEnginge(6, "#555555", "white");
    try {
      document.getElementById("reload_href")?.click();
    } catch {
      /* optional DOM refresh for captcha widget */
    }
  };

// ================= PLATFORM INFO =================
/** Client metadata for audit; IP comes from server `req.ip` (no external ipify call). */
const getPlatformInfo = () => ({
  browser: platform.name || "",
  browserVersion: platform.version || "",
  os: platform.os?.family || "",
  osVersion: platform.os?.version || "",
  device: platform.product || "Desktop",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  userAgent: platform.ua || "",
  ipAddress: "",
});

const newRequestId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;


/** GET /api/security/nonce — har login se pehle fresh nonce */
const parseNonceFromResponse = (res) => {
  const d = res?.data;
  return d?.data?.nonce ?? d?.nonce ?? null;
};

const fetchSecurityNonce = async () => {
  const res = await axios.get(`${API_URL}api/security/nonce`, {
    withCredentials: true,
  });
  const n = parseNonceFromResponse(res);
  if (!n || String(n).trim() === "") {
    throw new Error("Invalid nonce response from server");
  }
  return String(n).trim();
};

/** Error body/header se naya nonce (retry ke liye) */
const extractNonceFromError = (error) => {
  const h = error?.response?.headers || {};
  const fromHeader =
    h["x-new-request-nonce"] ??
    h["X-New-Request-Nonce"];
  const fromBody =
    error?.response?.data?.data?.nonce ?? error?.response?.data?.nonce ?? null;
  const v = fromHeader ?? fromBody;
  return v != null && String(v).trim() !== "" ? String(v).trim() : null;
};

const isNonceErrorCode = (error) => {
  const code = error?.response?.data?.code;
  return (
    code === "NONCE_INVALID" ||
    code === "NONCE_REQUIRED" ||
    code === "REPLAY_DETECTED"
  );
};

  async function submitButtonClick(e){
    
    e.preventDefault();
    if (!isNameValid || !isPasswordsValid) {
     toast.error('User Id and Password are required',tosterStyle);
      return;
    } 
   
    // Captcha check
    if (!captcha || captcha.trim().length === 0) {
      toast.error('Captcha is required', tosterStyle);
      setCaptcha('');
      loadCaptchaEnginge(6,'#555555','white');
      return;
    }
    if (validateCaptcha(captcha) === false) {
      toast.error('Captcha does not match', tosterStyle);
      setCaptcha('');
      loadCaptchaEnginge(6,'#555555','white');
      return;
    }

    const salt = bcrypt.genSaltSync(12);
    const password = bcrypt.hashSync(pwd, salt);
    const configuredApiKey = String(API_KEY || "").trim();
    // Fallback for environments where REACT_APP_API_KEY is not configured yet.
    // Keeps login usable while still using header-based auth.
    const apiKey = configuredApiKey || salt;
    
    showLoading(true);
    const platform_info = getPlatformInfo();
    try {
      let nonce = await fetchSecurityNonce();
      const signInPayload = {
        user_id: userId,
        password: password,
        platform_info: platform_info,
      };
      const signInHeaders = () => ({
        Authorization: `ApiKey ${apiKey}`,
        "X-Request-Nonce": nonce,
        "X-Request-Id": newRequestId(),
        "X-Request-Timestamp": Date.now().toString(),
      });

      let response;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          response = await axios.post(
            `${API_URL}api/auth/signin`,
            signInPayload,
            { headers: signInHeaders(), withCredentials: true }
          );
          break;
        } catch (err) {
          if (
            attempt === 0 &&
            isNonceErrorCode(err)
          ) {
            // Spec: NONCE_REQUIRED / NONCE_INVALID / REPLAY_DETECTED => fetch fresh nonce + retry once
            const nextNonce =
              extractNonceFromError(err) || (await fetchSecurityNonce());
            nonce = nextNonce; // throws if invalid response
            continue;
          }
          throw err;
        }
      }

      if (response?.status) {
        const apiOk = response.data?.status !== false;
        if (!apiOk) {
          toast.error(LOGIN_INVALID_CREDENTIALS_MSG, tosterStyle);
          resetFormAfterFailedLogin();
          return;
        }

        const dataBlock = response.data?.data;
        const accessToken =
          extractAccessTokenFromPayload(dataBlock) ||
          extractAccessTokenFromPayload(response.data) ||
          null;

        if (!AUTH_USE_HTTPONLY_COOKIE && !accessToken) {
          toast.error(LOGIN_INVALID_CREDENTIALS_MSG, tosterStyle);
          resetFormAfterFailedLogin();
          return;
        }

        const csrfFromSignin =
          response.data?.csrfToken ||
          dataBlock?.csrfToken ||
          null;
        if (csrfFromSignin) {
          setCsrfToken(String(csrfFromSignin).trim());
        }

        if (!AUTH_USE_HTTPONLY_COOKIE) {
          if (accessToken) {
            setToken(accessToken.trim());
          } else {
            toast.error(LOGIN_INVALID_CREDENTIALS_MSG, tosterStyle);
            resetFormAfterFailedLogin();
            return;
          }
        } else {
          if (accessToken) {
            setToken(accessToken.trim());
          } else {
            setToken('');
          }
        }

        const { user: userInfo, meta } = await resolveUserAfterSignin(
          dataBlock,
          accessToken
        );
        if (!userInfo?.id) {
          const msg =
            meta === "server_error" || meta === "network"
              ? "Login verified but session check failed (server error). Please contact support or retry."
              : LOGIN_INVALID_CREDENTIALS_MSG;
          toast.error(msg, tosterStyle);
          resetFormAfterFailedLogin();
          return;
        }

        const statusFlag = userInfo.id.STATUS_FLAG;
        loginCallToParent({
          userInfo,
          token: accessToken || null,
        });

        if (statusFlag === "U") {
          navigate("/changePwd");
        } else {
          navigate("/dashboard");
        }
      } else {
        showLoading(false);
        toast.error(LOGIN_INVALID_CREDENTIALS_MSG, tosterStyle);
        setUserId("");
        setPassword("");
        loadCaptchaEnginge(6, "#555555", "white");
        setCaptcha("");
      }
    } catch (error) {
      showLoading(false);
      const cfg = error?.config || error?.response?.config;

      if (error?.message === "SIGNATURE_REQUIRED") {
        toast.error(
          "Security signature configuration missing. Please contact admin.",
          tosterStyle
        );
        resetFormAfterFailedLogin({ clearUserId: false });
      } else if (isNonceErrorCode(error)) {
        const msg =
          error?.response?.data?.message ||
          "Invalid session. Please try again.";
        toast.error(msg, tosterStyle);
      } else if (isCredentialFailureResponse(error)) {
        toast.error(LOGIN_INVALID_CREDENTIALS_MSG, tosterStyle);
      } else if (isSignInUrl(cfg) && error?.response?.status >= 500) {
        toast.error(
          "Unable to sign in. Please try again later.",
          tosterStyle
        );
      } else if (isNonceUrl(cfg)) {
        const msg =
          error?.response?.data?.message ||
          "Unable to login, Please try again later.";
        toast.error(msg, tosterStyle);
      } else {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Login failed";
        toast.error(msg, tosterStyle);
      }
      setUserId("");
      setPassword("");
      loadCaptchaEnginge(6, "#555555", "white");
      setCaptcha("");
      console.error("Error logging in:", error);
    } finally {
      showLoading(false);
    }
}

 useEffect(() => {
      loadCaptchaEnginge(6,'#555555','white');
    },[]);

  const refreshCaptcha= ()=>{
      document.getElementById("reload_href").click();
    }

 const restForm=(e)=>{
     e.preventDefault();
      setUserId("");
      setPassword("");
      setCaptcha("");
     
    }

 
  const isNameValid = userId.trim().length > 0; 
  const isPasswordsValid = pwd.trim().length > 0;
 
  return (
    <>
    <div className="loginbg d-flex justify-content-center align-items-center">
       <GlobalLoading />
       <ToastContainer />
       
    <form>
      <div className="login_box">
        <div className="row">
          <div className="col-12 text-center">
            <p className="mb-0"><a href="index.html"><img src="assets/images/logo.png" className="logo" alt="UPDES"/></a></p>
            <h5 className="mb-0 updesloin-text">Directorate of Economics and Statistics</h5>
            <h5 className="updesloin-subtext my-0 p-0 ">Government of Uttar Pradesh</h5>
            <h6 className="updesloin-subtext2">Statistical Information System</h6>
          </div>
           <div className="text-center">
            <h3 className="text-uppercase signin-title">Sign in</h3>
          </div>
          <div className="col-12 mb-4">
            <label>User ID</label>
            <input type="text" className="form-control" value={userId} onChange={(e) => {
              let value = e.target.value;
              // Remove leading and trailing spaces
              value = value.replace(/^\s+|\s+$/g, '');
              setUserId(value);
            }}  placeholder="Enter your user id"
            maxLength={50}/>
          </div>
          
          <div className="col-12 mb-4">
            <label>Password</label>
            <div className="position-relative">
            <input type={showPassword ? "text" : "password"} className="form-control" value={pwd} onChange={(e) => {
              let value = e.target.value;
              // Remove leading and trailing spaces
              value = value.replace(/^\s+|\s+$/g, '');
              setPassword(value);
            }} placeholder="Enter your password" maxLength={25}/>
              <a href="#!" className="eye_password" onClick={togglePasswordVisibility}>
              {showPassword ? (
                 <i className="fa fa-eye"></i>
                 ) : (
                  <i className="fa fa-eye-slash"></i>
                )}
                
               </a>
            </div>
           </div>
  
     </div>
        
        <div className="row align-items-center mb-4">
          <div className="col-5">
            <input type="text" className="form-control" value={captcha} onChange={(e)=>setCaptcha(e.target.value)} placeholder="Enter captcha"/>
          </div>
         
           <div className="col-5 px-0">
            <LoadCanvasTemplate reloadText="Reload"  />
           </div>
          
          <div className="col-2 font14 text-center ps-0"><a  onClick={refreshCaptcha} href="#!">
            <img width="27" height="27" src="assets/images/refresh.svg" alt=""/></a>
          </div>
          
          <div className="col-12 d-none">
            <small className="text-danger">Captcha mismatch. try again!</small>
          </div>
          
        </div>
        
        <div className="row align-items-center">
          <div className="col-6">
            <button type="submit" onClick={submitButtonClick} className="btn w-100 btn_orange" >Log
              in</button>
          </div>
          
          <div className="col-6 font14 text-end">
            <button onClick={restForm} type="reset" className="btn w-100 btn_gray">Reset</button>
         </div>
          
          {/* <div className="col-12 text-center mt-4">
            <a target="_blank" href="https://onedrive.live.com/?authkey=%21AHjy6gkX5uBpa%2Dk&id=B6754D9BC49DF11F%211345&cid=B6754D9BC49DF11F" className="login-download-link" rel="noreferrer">Download OffLine Version <img className="d-inline-block ms-1" src="assets/images/download.svg" alt=""/></a>
          </div> */}
        </div>
        
      </div>
      <div className="col-12 text-center copyright-text">
           Copyright © {currentYear}&nbsp;UPDES.  All Rights Reserved.
    </div>
      
    </form>
   </div>

</>
  );
};

export default Login;
